import { Store } from './store';

export class MenuGroup {
    name: string;
    parent: string;
    store: number;
}

export class MenuItem {
    id:number;
    name: string;
    display_name:string;
    portion: string;
    description: string;
    base_price: number;
    pos_name: string;
    menu_item_group: number;
    store: number;
    is_available:boolean;
    available_qty:number;
    is_pos_only:boolean;
    is_taxable:boolean;
    show_on_catering_menu:boolean;
    hide_from_online_ordering:boolean;
}

export class ModifierGroup {
    id:number;
    name: string;
    display_name?: string;
    max_mods: number;
    min_available_modifiers?: number;
    is_required: boolean;
    store: number;
    pre_mod_tags: boolean;
    options: ModifierOption[]
    is_independent: any;
}

export class ModifierOption {
    id:number;
    name: string;
    modifier_group: number;
    upcharge: number;
    store: number;
    is_available:boolean;
    has_quantity:boolean;
    has_half_mod:boolean;
    max_mod_units:number;
    half_mod_upcharge: any;
    fixed_price:boolean;
    mod_type:string;
    mod_tag: string;
    no_tag_upcharge: number;
    lite_tag_upcharge: number;
    extra_tag_upcharge: number;
    onside_tag_upcharge: number;
    sub_tag_upcharge: number;

    // Nested sub-modifier groups attached by the menu read endpoint when the
    // `sub_modifiers_enabled` flag is on. Recursive, up to 5 levels deep.
    sub_modifier_groups?: ModifierGroup[];

    getUpcharge(quantity): number{
      if (this.fixed_price) {
        return +this.upcharge * 1;
      } else {
        return +this.upcharge * quantity;
      }
    }

    getUpcharge1(quantity): number{
      if (this.fixed_price) {
        return +this.half_mod_upcharge * 1;
      } else {
        return +this.half_mod_upcharge * quantity;
      }
    }
  }

export class Modifier {
    modifier: ModifierOption;
    modifier_raw: string;
    quantity:number;
    mod_type:string;
    price:number;
    max_mod_units:number;
    has_half_mod:boolean;
    has_quantity:boolean;
    ts_submitted:boolean;
    mod_tag: string;

    // Parent linkage for nested sub-modifier selections. The backend reads
    // this exact camelCase key (accepted by every submit path) and records
    // the parent → child hierarchy on the order. Undefined/null for
    // top-level modifiers.
    parentModifierId?: number;

    // ── Display side of the same hierarchy, sent back by the backend ──────
    // Row id of this order's modifier row (not the catalog option id).
    id?: number;
    // The modifier ROW this one hangs under — the read-side counterpart of
    // `parentModifierId` above, which is an OPTION id used only on submit.
    parent_parser_order_modifier?: number | null;
    // Nesting level for display: 0 for a normal modifier, 1+ for a
    // sub-modifier. Absent when the store's sub-modifier flag is off.
    depth?: number;

    getUpcharge(): number{
      this.modifier = Object.assign(new ModifierOption(), this.modifier)
      if (!this.modifier || this.mod_type.toLowerCase() === 'no' || this.mod_type.toLowerCase() === 'remove'){
        return 0;
      } else {
        if (this.mod_type == "H1" || this.mod_type == "H2") {
          if (this.modifier.half_mod_upcharge != null) {
            let upcharge = this.modifier.getUpcharge1(this.quantity);
            return upcharge
          } else {
            let upcharge = this.modifier.getUpcharge(this.quantity);
            return upcharge
          }
      } else {
        let upcharge = this.modifier.getUpcharge(this.quantity);
        return upcharge
      }
    }
  }
}

export class Order {
  id:number;
  bot_order: number;
  quantity: number;
  portion: string;
  menu_item: MenuItem;
  menu_item_raw: string;
  special_request: string;
  ts_submitted:boolean;
  sale_price: number;
  modifiers: Modifier[];
  is_removed: boolean;

  getItemTotal(){
    this.menu_item = Object.assign(new MenuItem(), this.menu_item);
    let itemTotal = 0
    itemTotal = +this.menu_item.base_price * this.quantity;
    for(let mod of this.modifiers) {
      itemTotal = itemTotal + (mod.price * (mod.modifier.fixed_price ? 1 : mod.quantity));
    }
    return itemTotal;
  }

  getAvailableQty(){
    if(this.menu_item.available_qty !== null){
      return this.menu_item.available_qty;
    }else{
      return 500;
    }
  }
}

export class MiscItem{
  quantity:number;
  item_name:string;
  price:number;
  ts_submitted:boolean;
}

// One additional tax line (e.g. Oklahoma "Mixed beverage tax 13.5%") returned by the backend
// alongside the regular sales tax. Amounts arrive as decimal strings.
export interface AdditionalTaxLine {
  id: number;
  name: string;
  label: string;
  rate_percent: string;
  taxable_base: string;
  amount: string;
  card_amount: string;
  cash_amount: string;
}

// One auto gratuity line (ENG-5810/ENG-5812): a rule the backend applied to this check, e.g.
// "Event Gratuity 20%". The amount is a tip (never taxed, same figure for cash and card) and is
// already inside payment.total / cash_due_total; the app only displays it. Decimal strings on the wire.
export interface AutoGratuityLine {
  id: number;
  name: string;
  label: string;
  amount_type: 'PERCENT' | 'FIXED' | string;
  rate_percent: string | null;
  fixed_amount: string | null;
  basis: string;
  base: string;
  amount: string;
  status: 'AUTO' | 'MANUAL' | string;
  guest_count: number | null;
}

export class Payment{
  auth_number: string;
  date:Date;
  sub_total:number;
  tax: number;
  // Sum of additional taxes (card / cash) and the per-line breakdown; absent when the store has none
  // (decimal strings on the wire; OrderHelpers.parsePayment coerces them to numbers)
  additional_tax?: string | number;
  cash_additional_tax?: string | number;
  additional_taxes?: AdditionalTaxLine[];
  // Auto gratuity: the sum (inside total) and its lines; absent when no rule applies
  // (OrderHelpers.parsePayment coerces auto_gratuity to a number when present)
  auto_gratuity?: string | number;
  auto_gratuities?: AutoGratuityLine[];
  auto_gratuities_waived?: { id: number; name: string; label: string }[];
  tip:number;
  total:number;
  type:string;
  delivery_charge:number;
  store_credit:number;
  service_charge:number;
  discount:number;
  promotional_discount:number;
  surcharge:number;
  // ENG-5889: the flat order fee already inside sub_total, and its label for the disclosure.
  order_fee:number;
  order_fee_label:string;
  order_fee_display:string;
  cash_due_total:number;
}

export class EditModifierOptions{
  parsed_menu_item: Order;

  modifier_groups:Map<string, ModifierOption[]>;
}

export class ModifierGroups{
  menu_item: MenuItem;
  modifier_groups:ModifierGroup[];
}

export class BotOrder {
  // ENG-5514: third-party courier status (set by the KitchenHub webhook); staging's model already has it
  delivery_status_doordash?: number | string | null;
  id: number;
  phone_number: string;
  store: number;
  account: number;
  time: string;
  time_raw: string;
  occasion: string;
  occasion_raw: string;
  occasion_schedule:string;
  delivery_address:string;
  order: string;
  special_request: string;
  order_hash: string;
  dine_in_table_number: string;
  is_submitted: boolean;
  is_temp:boolean;
  is_parsed:boolean;
  order_counter:string;
  phone_number_verified:boolean;
  encoded_id:string;
  curbside_vehicle_id:string;
  curbside_drop_off:boolean;
  order_name:string;
  failure_reason:string;
  is_tab_open:boolean;
  is_expired: boolean;
  promo_code: string;

  created_at: Date;
  updated_at: Date;
  submitted_at:Date;
  tip_percent:number;
  online_service_charge:any;
  view_notified: boolean;
  is_cancelled: any;
  ready_notified: boolean;
  payment_type: any;
  delivery_status:any
  is_payment_pending:boolean;
  has_submitted_tab:boolean;
  is_kiosk:boolean;
  is_catering:boolean;
  is_reorder:boolean;
  reorder_source_hash:string;

  // ENG-5252: customer self-serve cancellation (server-computed eligibility)
  can_customer_cancel: boolean;
  customer_cancel_seconds_remaining: number;
  customer_cancellable_until: string;
  cancelled_by: string;
  is_future_order: boolean;
}

// ENG-5252: per-store cancellation settings delivered on the store payload
export class CustomerOrderCancelSettings {
  enabled: boolean;
  window_seconds: number;
  future_window_minutes: number;
}
export class OrdersWithPayment{
  bot_order:BotOrder;
  store:Store;
  items:Order[];
  misc_items: MiscItem[];
  payment: Payment;

  applyStoreCredit(amount){
    return this.payment.store_credit < this.payment.total ? this.payment.total : this.payment.store_credit - this.payment.total
  }

  // Additional taxes (card amount) folded into the recomputed total; 0 when the store has none
  getAdditionalTax(){
    return +(this.payment.additional_tax || 0) || 0;
  }

  // Auto gratuity folded into the recomputed total; 0 when no rule applies. It is a tip on top
  // of the guest's own tip, so the tip percent buttons never use it as their base.
  getAutoGratuity(){
    return +(this.payment.auto_gratuity || 0) || 0;
  }

  updateTip(tipPercentage:number){
    this.bot_order.tip_percent = tipPercentage;
    // Percent tips are on the subtotal WITH the order fee (David 2026-10-24): sub_total already
    // holds it in "subtotal" mode; in "line" mode it sits outside sub_total and is added back.
    this.payment.tip = +((+this.payment.sub_total + (this.orderFeeIsLine() ? (+this.payment.order_fee || 0) : 0)) * (+tipPercentage / 100)).toFixed(2)
    this.payment.total = +(this.payment.sub_total + this.payment.tax + this.getAdditionalTax() + this.getAutoGratuity() + this.payment.tip).toFixed(2);
  }

  updateTipCustom(tip:number){
    this.payment.tip = +(tip).toFixed(2)
    this.payment.total = +(this.payment.sub_total + this.payment.tax + this.getAdditionalTax() + this.getAutoGratuity() + this.payment.tip).toFixed(2);
  }

  getItemCount(){
    return this.items.filter(i => !i.is_removed).reduce((sum,item) => sum + item.quantity, 0)
  }

  isValidOrder(){
    if(this.bot_order.occasion === 'DELIVERY'){
      return this.payment.type !== 'STORE_CREDIT' ? (this.payment.total + this.payment.store_credit) >= this.store.delivery_minimum : true
    }else if(this.bot_order.occasion === 'TABLESIDE'){
      return this.bot_order.dine_in_table_number ? true : false;
    }else if(this.bot_order.occasion === 'CURBSIDE'){
      return this.bot_order.curbside_vehicle_id ? true : false;
    }else{
      return true;
    }
  }

  /** ENG-5889: the fee shows as its own row after the subtotal ("line" mode). */
  orderFeeIsLine(): boolean {
    return +(this.payment?.order_fee || 0) > 0 && (this.payment?.order_fee_display || '').toLowerCase() === 'line';
  }

  /** The row label in line mode: the store's label with a capital first letter. */
  getOrderFeeRowLabel(): string {
    const label = (this.payment?.order_fee_label || '').trim() || 'order fee';
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  /** ENG-5889: "Subtotal includes a $0.39 order fee." or '' (fee inside the subtotal only). */
  getOrderFeeNote(): string {
    const fee = +(this.payment?.order_fee || 0);
    if (!(fee > 0) || this.orderFeeIsLine()) { return ''; }
    const label = (this.payment?.order_fee_label || '').trim() || 'order fee';
    return `Subtotal includes a $${fee.toFixed(2)} ${label}.`;
  }

  getOriginalTotal(){
    return this.payment.tip + this.payment.sub_total + this.payment.tax + this.getAdditionalTax() + this.getAutoGratuity() + this.payment.delivery_charge
  }

  getTipPercent(){
    return +this.bot_order.tip_percent;
  }

  getTipPercentOptions(){
    return this.store.tip_percent_option.sort((a,b) => a.percent - b.percent);
  }

  getTableSideItemCount(){
    return this.items.filter(i => !i.ts_submitted && !i.is_removed).reduce((sum,item) => sum + item.quantity, 0)
  }

  isTabOpen(){
    return this.bot_order.is_tab_open;
  }

  getOccasion(){
    return this.bot_order.occasion;
  }

  getOccasionDetail(){
    return new OccasionDetail(this.bot_order.occasion, this.bot_order.time,
      this.bot_order.occasion_schedule,this.bot_order.dine_in_table_number,
      this.bot_order.delivery_address, this.bot_order.curbside_vehicle_id)
  }

  setOccasionDetail(occasionDetail:OccasionDetail){
    this.bot_order.occasion = occasionDetail.occasion;
    this.bot_order.time = occasionDetail.time;
    this.bot_order.occasion_schedule = occasionDetail.occasion_schedule;
    this.bot_order.dine_in_table_number = occasionDetail.dine_in_table_number;
    this.bot_order.delivery_address = occasionDetail.delivery_address;
    this.bot_order.curbside_vehicle_id = occasionDetail.curbside_vehicle_id;
  }

  getTableNumber(){
    return this.bot_order.dine_in_table_number.trim();
  }
}

export class MenuItemGroupImage {
  id?: number;
  name: string;
  /** 600x600 square, for menu list tiles. */
  thumbnail: string;
  /** Long edge <= 1200px, for the item detail view. Absent on rows not yet backfilled. */
  medium?: string;
  /** Bounded original (<= 2400px). Fallback when `medium` is missing. */
  image: string;
  created_at?: string;
}

export class MenuItemGroup{
  id:number;
  name:string;
  description:string;
  starting_price:number;
  store:number;
  menu_group:MenuGroup;
  hide_from_online_ordering: boolean;
  show_on_catering_menu: boolean;
  /** Newest first. Returned by /store/:id/menu_item_group/:id/. */
  latest_image?: MenuItemGroupImage[];
}

/**
 * Best image for a menu list tile from an item's `last_image` (a list online, a dict on POS).
 * Prefers the 600px thumbnail; while a row still carries the legacy 100px thumbnail
 * (pre-backfill) it prefers `medium` instead. Never returns the full original — that can
 * be a 10 MB file and has no place in a list.
 */
export function bestListThumbnail(lastImage: MenuItemGroupImage[] | MenuItemGroupImage | null | undefined): string | null {
  const first = Array.isArray(lastImage) ? lastImage[0] : lastImage;
  if (!first) return null;
  const legacyThumb = !!first.thumbnail && first.thumbnail.includes('-100-100.');
  if (first.thumbnail && !legacyThumb) return first.thumbnail;
  return first.medium || first.thumbnail || null;
}

/** The only brand artwork that exists under `img/cc/`; everything else falls back to `default`. */
const CARD_BRAND_ICONS = ['visa', 'mastercard', 'amex', 'discover', 'diners', 'jcb'];

/** Gateways spell the same brand several ways; map the ones we see onto the files we ship. */
const CARD_BRAND_ALIASES: { [key: string]: string } = {
  master: 'mastercard',
  mc: 'mastercard',
  americanexpress: 'amex',
  americanexp: 'amex',
  dinersclub: 'diners',
  discovercard: 'discover',
};

/**
 * Brand icon for a saved card. `card_type` comes straight from the gateway, so it can be a
 * brand we have no artwork for, a different spelling of one we do ('MASTER'), or missing
 * entirely — all of which used to render a broken-image glyph next to the card's last four.
 */
export function cardBrandIcon(cardType: string | null | undefined): string {
  const slug = (cardType || '').toLowerCase().replace(/[^a-z]/g, '');
  const brand = CARD_BRAND_ALIASES[slug] || slug;
  const file = CARD_BRAND_ICONS.includes(brand) ? brand : 'default';
  return `https://storage.googleapis.com/menus/static/frontend/img/cc/${file}.svg`;
}

export class CardDetails{
    id:number;
    name_on_card:string;
    card_type: string;
    phone_number:string;
    // ENG-5611: token and cvc are no longer returned by the API. A card is identified by id and
    // shown by last_4; neither the card reference nor the verification value belongs in a browser.
    expiry:string;
    store:number;
    expiryMonth:string;
    expiryYear:string;
    last_4:string;
    gateway:string;
    is_deleted: boolean;
    updated_at: Date;
    created_at: Date;
    nmi_customer_vault_id: string;
    zip_code: number;
    is_avs_verified: boolean | null;

    getExpMonth(){
      return this.expiry.length === 5 ? `0${this.expiry.slice(0, 1)}` : this.expiry.slice(0, 2);
    }

    getExpYear(){
      return this.expiry.slice(-2);
    }

    hasExpired(){
      const today = new Date();
      const month = this.expiry.slice(0, 2);
      const year = `20${this.expiry.slice(-2)}`;

      return today.getMonth() > +month && today.getFullYear() >= +year;
    }
  }

export class PaymentMethods{
  cards:Map<string, CardDetails[]>;
}

export class HostedMenu{
  upload_file:string;
}

export class OccasionDetail{
  occasion:string;
  time:string;
  occasion_schedule:string;
  delivery_address:string;
  dine_in_table_number:string;
  curbside_vehicle_id:string;
  location_selected:boolean;

  constructor(occasion, time='NOW', schedule=null,
    tableId=null, address=null, vehicleId=null){
    this.occasion = occasion
    this.time = time
    this.occasion_schedule = schedule
    this.dine_in_table_number = tableId
    this.delivery_address = address
    this.curbside_vehicle_id = vehicleId
  }

  isEmpty(value){
    return (typeof value === "undefined" || value === null || value === "");
  }

  isValid(){
    let valid = false;
      switch(this.occasion){
        case 'PICKUP':
          valid = true
          break;
        case 'DELIVERY':
          valid = !this.isEmpty(this.delivery_address) && this.location_selected
          break;
        case 'CURBSIDE':
          valid = !this.isEmpty(this.curbside_vehicle_id)
          break;
        case 'DINE IN':
          valid = !this.isEmpty(this.dine_in_table_number)
          break;
        case 'TABLESIDE':
          valid = !this.isEmpty(this.dine_in_table_number)
          break;
      }

      if(this.time === 'LATER'){
        return valid && !this.isEmpty(this.occasion_schedule)
      }else if(this.time === 'NOW'){
        return valid;
      }else{
        return false; // time not selected yet
      }
  }
}
